import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { useToastStack, ToastStack } from "../../../../shared/components/ActionToast";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { createWorkOrder } from "../../store/workOrdersSlice";
import {
  WorkOrderTypeCode,
  WorkOrderPriorityCode,
  CreateWorkOrder as CreateWorkOrderModel,
} from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";

/* ── Priority chip color map (matches card stripe system) ── */
const PRIORITY_COLORS: Record<number, { dot: string; border: string; bg: string }> = {
  [WorkOrderPriorityCode.Critical]: { dot: "#D4572B", border: "#D4572B", bg: "rgba(212,87,43,0.06)" },
  [WorkOrderPriorityCode.High]:     { dot: "#E8A817", border: "#E8A817", bg: "rgba(232,168,23,0.06)" },
  [WorkOrderPriorityCode.Normal]:   { dot: "#B8B5AF", border: "#B8B5AF", bg: "rgba(184,181,175,0.08)" },
  [WorkOrderPriorityCode.Low]:      { dot: "#3B82F6", border: "#3B82F6", bg: "rgba(59,130,246,0.06)" },
};

interface QuickCreateInlineProps {
  onCreated: () => void;
  onCancel: () => void;
}

const QuickCreateInline = ({ onCreated, onCancel }: QuickCreateInlineProps) => {
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [priorityCode, setPriorityCode] = useState<number>(WorkOrderPriorityCode.Normal);
  const [typeId, setTypeId] = useState<number>(WorkOrderTypeCode.Reactive);
  const [submitting, setSubmitting] = useState(false);
  const { addToast, dismissToast, toasts } = useToastStack();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const priorityOptions = [
    { code: WorkOrderPriorityCode.Critical, label: translate("priorities.critical") },
    { code: WorkOrderPriorityCode.High,     label: translate("priorities.high") },
    { code: WorkOrderPriorityCode.Normal,   label: translate("priorities.normal") },
    { code: WorkOrderPriorityCode.Low,      label: translate("priorities.low") },
  ];

  const typeOptions = [
    { code: WorkOrderTypeCode.Reactive, label: translate("typesShort.reactive") },
    { code: WorkOrderTypeCode.PreventiveMaintenance, label: translate("typesShort.preventiveMaintenance") },
    { code: WorkOrderTypeCode.PredictiveMaintenance, label: translate("typesShort.predictiveMaintenance") },
    { code: WorkOrderTypeCode.Inspection, label: translate("typesShort.inspection") },
    { code: WorkOrderTypeCode.GeneralTask, label: translate("typesShort.generalTask") },
  ];

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || submitting) return;

    setSubmitting(true);
    const model: CreateWorkOrderModel = {
      title: title.trim(),
      workOrderTypeId: typeId,
      priorityCode,
    };

    try {
      const result = await dispatch(createWorkOrder(model)).unwrap();
      addToast("success", translate("quickCreate.created", { number: result.number }), { duration: 3000 });
      setTitle("");
      setPriorityCode(WorkOrderPriorityCode.Normal);
      setTypeId(WorkOrderTypeCode.Reactive);
      onCreated();
    } catch {
      // Error handled by Redux
    } finally {
      setSubmitting(false);
    }
  }, [title, typeId, priorityCode, submitting, dispatch, translate, onCreated]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        onCancel();
      }
    },
    [handleSubmit, onCancel]
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          animation: "fadeSlideUp 0.2s ease both",
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Title input */}
        <input
          ref={inputRef}
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={translate("quickCreate.titlePlaceholder")}
          style={{
            border: "1.5px solid #E8E5DF",
            borderRadius: "10px",
            padding: "14px 16px",
            fontSize: "14px",
            fontWeight: 400,
            fontFamily: "'Sora', sans-serif",
            color: "#0C1117",
            background: "#F7F5F0",
            outline: "none",
            width: "100%",
            boxSizing: "border-box",
            transition: "border-color 0.2s ease",
          }}
          onFocus={(e) => {
            (e.target as HTMLInputElement).style.borderColor = "#D4572B";
          }}
          onBlur={(e) => {
            (e.target as HTMLInputElement).style.borderColor = "#E8E5DF";
          }}
        />

        {/* Priority selector */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginTop: "20px",
            marginBottom: "10px",
          }}
        >
          {translate("fields.priority")}
        </Box>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {priorityOptions.map((opt) => {
            const sel = priorityCode === opt.code;
            const c = PRIORITY_COLORS[opt.code];
            return (
              <Box
                key={opt.code}
                onClick={() => setPriorityCode(opt.code)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: sel ? `1.5px solid ${c.border}` : "1.5px solid #E8E5DF",
                  backgroundColor: sel ? c.bg : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: c.border,
                    backgroundColor: c.bg,
                  },
                }}
              >
                <Box
                  sx={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: c.dot,
                    flexShrink: 0,
                  }}
                />
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: sel ? 600 : 500,
                    color: sel ? "#0C1117" : "#4A4F57",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Type selector */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 600,
            color: "#8C9099",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginTop: "20px",
            marginBottom: "10px",
          }}
        >
          {translate("fields.maintenanceType")}
        </Box>
        <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {typeOptions.map((opt) => {
            const sel = typeId === opt.code;
            return (
              <Box
                key={opt.code}
                onClick={() => setTypeId(opt.code)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: sel ? "1.5px solid #D4572B" : "1.5px solid #E8E5DF",
                  backgroundColor: sel ? "rgba(212,87,43,0.06)" : "transparent",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "#D4572B",
                    backgroundColor: "rgba(212,87,43,0.06)",
                  },
                }}
              >
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: sel ? 600 : 500,
                    color: sel ? "#D4572B" : "#4A4F57",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            paddingTop: "24px",
            marginTop: "auto",
          }}
        >
          {/* Keyboard hint */}
          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "10px",
              fontWeight: 400,
              color: "#8C9099",
              lineHeight: 1.6,
              marginRight: "auto",
            }}
          >
            {translate("quickCreate.enterToSubmit")}
            <br />
            {translate("quickCreate.escToCancel")}
          </Box>
          {/* Cancel */}
          <Box
            onClick={onCancel}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: "#4A4F57",
              cursor: "pointer",
              padding: "8px 12px",
              transition: "opacity 0.15s ease",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {translate("quickCreate.cancel")}
          </Box>
          {/* Create button */}
          <Box
            onClick={handleSubmit}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              color: "#FFFFFF",
              cursor: submitting || !title.trim() ? "not-allowed" : "pointer",
              padding: "10px 24px",
              borderRadius: "8px",
              backgroundColor: submitting || !title.trim() ? "#B8B5AF" : "#D4572B",
              transition: "all 0.15s ease",
              opacity: submitting || !title.trim() ? 0.6 : 1,
              "&:hover":
                submitting || !title.trim()
                  ? {}
                  : { backgroundColor: "#BF4D26" },
            }}
          >
            {translate("quickCreate.create")}
          </Box>
        </Box>
      </Box>

      {/* Stacking toast notifications */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </>
  );
};

export default QuickCreateInline;
